/**
 * Turning millionths into something a person reads - pure, and tested.
 *
 * Two decisions here, and both are the kind that look cosmetic and are not:
 *
 *  1. HOW MANY DECIMALS. A month's telephony is a couple of dollars and one call
 *     is a fraction of a cent. Two decimals everywhere renders every single call
 *     as "$0.00", which is not a rounding artefact but a screen that says the
 *     calls are free. So the number of decimals follows the size of the number,
 *     and the rule is stated once here rather than guessed at each call site.
 *  2. WHAT AN ABSENT NUMBER LOOKS LIKE. `null` means nobody has priced it, and
 *     it has to draw as a dash rather than as zero. The whole value of a cost
 *     screen is that the figure on it can be trusted.
 */

/** How the two report periods are offered. Same shape as the coverage screen. */
export const COST_DAYS = [7, 30, 90] as const;
export type CostDays = (typeof COST_DAYS)[number];

/** The period a preset covers, ending now. */
export function costPeriod(days: CostDays, now: Date): { from: Date; to: Date } {
  return { from: new Date(now.getTime() - days * 24 * 60 * 60 * 1000), to: now };
}

/**
 * Millionths as a decimal string, with the precision the size deserves.
 *
 * Under a cent, four places - anything less prints every individual call as
 * nothing. From a cent up, two places, because that is what a total is read as
 * and four would be noise on a figure somebody is comparing to an invoice.
 *
 * Never a currency symbol: the unit comes from Twilio and may be EUR, so the
 * caller pairs this with the unit it was given rather than assuming a dollar.
 */
export function fmtMicros(micros: number | null | undefined): string | null {
  if (micros === null || micros === undefined || !Number.isFinite(micros)) return null;
  const amount = micros / 1_000_000;
  if (amount === 0) return "0.00";
  return amount < 0.01 ? amount.toFixed(4) : amount.toFixed(2);
}

/**
 * A money bucket as "1.23 USD", or null when there is nothing to show.
 *
 * An empty list is null rather than "0.00": a period with no priced call yet has
 * not been shown to cost nothing, and the two must not read the same.
 */
export function fmtMoney(buckets: readonly { unit: string; micros: number }[]): string | null {
  if (buckets.length === 0) return null;
  return buckets
    .map((bucket) => `${fmtMicros(bucket.micros) ?? "0.00"} ${bucket.unit}`)
    .join(" + ");
}

/**
 * Seconds as "1 h 12 min" / "12 min" / "48 s".
 *
 * Hours appear only once there are any, because "0 h 12 min" is a longer way of
 * writing twelve minutes and a column of them is harder to scan.
 */
export function fmtDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours} h ${minutes} min`;
  if (minutes > 0) return `${minutes} min`;
  return `${total} s`;
}

/**
 * Tokens as "412k" / "1.2M" / "980".
 *
 * Shortened because the exact count is never the question - the question is
 * whether it is thousands or millions, and a nine digit number in a table cell
 * answers that more slowly than three characters do.
 */
export function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.max(0, Math.round(n)));
}
